import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Users, Clock, AlertTriangle, UserMinus, ChevronRight } from 'lucide-react';
import type { PaymentMethod, TrendMetric, HealthBand } from '@techsofit/api-types';
import { Button, Card, StatCard, Skeleton, EmptyState, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { TrendChart } from '../components/TrendChart';
import { getRevenueReport, getMembershipReport, getTimeseries, getBusinessHealth, downloadReportCsv } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { money, thisMonthIso } from '../lib/format';

const METRICS: { key: TrendMetric; label: string }[] = [
  { key: 'REVENUE', label: 'Revenue' },
  { key: 'ATTENDANCE', label: 'Check-ins' },
  { key: 'PT_REVENUE', label: 'PT revenue' },
  { key: 'NEW_MEMBERS', label: 'New members' },
];

const RANGES: { d: number; label: string; human: string }[] = [
  { d: 7, label: '7d', human: '7 days' },
  { d: 30, label: '30d', human: '30 days' },
  { d: 90, label: '90d', human: '90 days' },
  { d: 365, label: '1y', human: '12 months' },
];

const METHOD_LABEL: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  ECOCASH: 'EcoCash',
  ONEMONEY: 'OneMoney',
  INNBUCKS: 'InnBucks',
  BANK_TRANSFER: 'Bank transfer',
  ONLINE: 'Online',
  OTHER: 'Other',
};

const monthLabel = (ym: string) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' });

const currentMonth = () => thisMonthIso();

export function ReportsPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'month' | 'week'>('month');
  const [months, setMonths] = useState(6);
  const [weekMonth, setWeekMonth] = useState(currentMonth());

  const revenue = useAsync(
    () => getRevenueReport(mode === 'week' ? { weekMonth } : { months }),
    [mode, months, weekMonth],
  );
  const membership = useAsync(getMembershipReport);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Interactive trends chart: one metric at a time over a chosen trailing window.
  const [metric, setMetric] = useState<TrendMetric>('REVENUE');
  const [rangeDays, setRangeDays] = useState(30);
  const trends = useAsync(() => getTimeseries(metric, rangeDays), [metric, rangeDays]);
  const ts = trends.data;
  const metricLabel = METRICS.find((x) => x.key === metric)!.label;
  const rangeHuman = RANGES.find((x) => x.d === rangeDays)!.human;
  const totalLabel = ts
    ? ts.unit === 'MONEY'
      ? `${money(ts.total)} total`
      : `${Number(ts.total).toLocaleString()} ${metricLabel.toLowerCase()}`
    : '';

  async function exportCsv(type: 'members' | 'payments') {
    setDownloading(type);
    try {
      // Members roster is always full; payments honour the visualised window.
      const r = revenue.data;
      const range = r ? { from: r.from, to: r.to } : undefined;
      await downloadReportCsv(type, type === 'payments' ? range : undefined);
    } finally {
      setDownloading(null);
    }
  }

  // Weekly buckets are labelled W1, W2, … by order; monthly use the month name.
  const barLabel = (b: { month: string }, i: number) =>
    mode === 'week' ? `W${i + 1}` : monthLabel(b.month);

  const r = revenue.data;
  const peak = r ? Math.max(1, ...r.byMonth.map((m) => Number(m.amount))) : 1;
  const methodPeak = r ? Math.max(1, ...r.byMethod.map((m) => Number(m.amount))) : 1;
  const m = membership.data;
  const planPeak = m ? Math.max(1, ...m.byPlan.map((p) => p.count)) : 1;

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow="Reports"
        title="Reports"
        description="Revenue and membership at a glance, plus CSV exports for your records."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" loading={downloading === 'members'} onClick={() => exportCsv('members')}>
              <Download className="size-4" aria-hidden />
              <span className="hidden sm:inline">Members CSV</span>
            </Button>
            <Button variant="outline" loading={downloading === 'payments'} onClick={() => exportCsv('payments')}>
              <Download className="size-4" aria-hidden />
              <span className="hidden sm:inline">Payments CSV</span>
            </Button>
          </div>
        }
      />

      <BusinessHealthCard />

      {/* Trends — interactive, one metric over a trailing window */}
      <Card className="mb-3 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-ink">Trends</h2>
            <p className="text-sm text-muted">{ts ? `${totalLabel} · last ${rangeHuman}` : 'Loading…'}</p>
          </div>
          <div className="flex gap-1.5">
            {RANGES.map((rng) => (
              <button
                key={rng.d}
                onClick={() => setRangeDays(rng.d)}
                aria-pressed={rangeDays === rng.d}
                className={cn(
                  'rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
                  rangeDays === rng.d ? 'bg-dark text-on-dark' : 'bg-surface text-ink-soft hover:bg-line-strong',
                )}
              >
                {rng.label}
              </button>
            ))}
          </div>
        </div>

        {/* Metric toggle */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {METRICS.map((mopt) => (
            <button
              key={mopt.key}
              onClick={() => setMetric(mopt.key)}
              aria-pressed={metric === mopt.key}
              className={cn(
                'rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
                metric === mopt.key ? 'bg-accent text-accent-ink' : 'bg-surface text-ink-soft hover:bg-line-strong',
              )}
            >
              {mopt.label}
            </button>
          ))}
        </div>

        {trends.loading ? (
          <Skeleton className="mt-6 h-64" />
        ) : trends.error || !ts ? (
          <EmptyState title="Couldn’t load trends" description={trends.error ?? ''} className="mt-4 bg-surface" />
        ) : (
          <div className="mt-4">
            <TrendChart
              points={ts.points.map((p) => ({ date: p.date, value: Number(p.value) }))}
              bucket={ts.bucket}
              unit={ts.unit}
              ariaLabel={`${metricLabel} over the last ${rangeHuman}`}
            />
          </div>
        )}
      </Card>

      {/* Revenue */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-ink">Revenue</h2>
            <p className="text-sm text-muted">{r ? `${money(r.total)} collected` : 'Loading…'}</p>
          </div>
          {/* Monthly / Weekly granularity */}
          <div className="flex gap-1.5 rounded-pill bg-surface p-1">
            {(['month', 'week'] as const).map((g) => (
              <button
                key={g}
                onClick={() => setMode(g)}
                aria-pressed={mode === g}
                className={cn(
                  'rounded-pill px-3 py-1 text-sm font-medium capitalize transition-colors',
                  mode === g ? 'bg-dark text-on-dark' : 'text-ink-soft hover:text-ink',
                )}
              >
                {g === 'month' ? 'Monthly' : 'Weekly'}
              </button>
            ))}
          </div>
        </div>

        {/* Range controls — presets for monthly, a month picker for weekly */}
        <div className="mt-3">
          {mode === 'month' ? (
            <div className="flex gap-1.5">
              {[3, 6, 12, 24].map((n) => (
                <button
                  key={n}
                  onClick={() => setMonths(n)}
                  aria-pressed={months === n}
                  className={cn(
                    'rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
                    months === n ? 'bg-dark text-on-dark' : 'bg-surface text-ink-soft hover:bg-line-strong',
                  )}
                >
                  {n}m
                </button>
              ))}
            </div>
          ) : (
            <label className="flex items-center gap-2 text-sm text-ink-soft">
              Month
              <input
                type="month"
                value={weekMonth}
                max={currentMonth()}
                onChange={(e) => setWeekMonth(e.target.value || currentMonth())}
                className="rounded-control border border-line bg-elevated px-3 py-1.5 text-sm text-ink"
              />
            </label>
          )}
        </div>

        {revenue.loading ? (
          <Skeleton className="mt-6 h-40" />
        ) : revenue.error || !r ? (
          <EmptyState title="Couldn’t load revenue" description={revenue.error ?? ''} className="mt-4 bg-surface" />
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
            {/* Monthly bars */}
            <div>
              <div className="flex h-40 items-end gap-2.5">
                {r.byMonth.map((mm, i) => (
                  <div key={mm.month} className="flex flex-1 flex-col items-center gap-2">
                    <div
                      className="w-full rounded-t-lg rounded-b-md bg-dark transition-all hover:bg-ink"
                      style={{ height: `${Math.max(2, Math.round((Number(mm.amount) / peak) * 100))}%` }}
                      title={`${money(mm.amount)} · ${mm.month}`}
                    />
                    <span className="text-xs text-muted">{barLabel(mm, i)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By method */}
            <div>
              <p className="mb-3 text-eyebrow uppercase text-muted">By method</p>
              {r.byMethod.length ? (
                <ul className="flex flex-col gap-2.5">
                  {r.byMethod.map((mm) => (
                    <li key={mm.method}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-ink-soft">{METHOD_LABEL[mm.method]}</span>
                        <span className="font-medium text-ink tabular">{money(mm.amount)}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface">
                        <div className="h-full rounded-full bg-accent" style={{ width: `${(Number(mm.amount) / methodPeak) * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted">No payments in this period.</p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Membership */}
      {membership.loading ? (
        <Skeleton className="mt-3 h-40" />
      ) : membership.error || !m ? (
        <EmptyState title="Couldn’t load membership" description={membership.error ?? ''} className="mt-3" />
      ) : (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              label="Active"
              value={m.active}
              icon={<Users className="size-4" aria-hidden />}
              actionLabel="View active"
              onClick={() => navigate('/members?filter=active')}
            />
            <StatCard
              label="Expiring"
              value={m.expiring}
              icon={<Clock className="size-4" aria-hidden />}
              emphasis
              actionLabel="View expiring"
              onClick={() => navigate('/members?filter=expiring')}
            />
            <StatCard
              label="Lapsed"
              value={m.lapsed}
              icon={<AlertTriangle className="size-4" aria-hidden />}
              actionLabel="View lapsed"
              onClick={() => navigate('/members?filter=lapsed')}
            />
            <StatCard
              label="Never paid"
              value={m.neverPaid}
              icon={<UserMinus className="size-4" aria-hidden />}
              actionLabel="View never paid"
              onClick={() => navigate('/members?filter=never_paid')}
            />
          </div>

          <Card className="mt-3 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-ink">Members by plan</h2>
              <span className="text-sm text-muted">{m.total} total · {m.newThisMonth} new this month</span>
            </div>
            <ul className="mt-4 flex flex-col gap-1">
              {m.byPlan.map((p) => (
                <li key={p.plan}>
                  <button
                    type="button"
                    onClick={() => navigate(`/members?plan=${encodeURIComponent(p.plan)}`)}
                    className="group flex w-full flex-col gap-1 rounded-control px-2 py-1.5 text-left transition-colors hover:bg-surface"
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-ink-soft group-hover:text-ink">
                        {p.plan}
                        <ChevronRight className="size-3.5 text-muted opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                      </span>
                      <span className="font-medium text-ink tabular">{p.count}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface group-hover:bg-line">
                      <div className="h-full rounded-full bg-dark" style={{ width: `${(p.count / planPeak) * 100}%` }} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </section>
  );
}

const BAND: Record<HealthBand, { label: string; stroke: string; text: string; bar: string }> = {
  GOOD: { label: 'Good', stroke: '#3F7D4E', text: 'text-ok', bar: 'bg-ok' },
  WATCH: { label: 'Watch', stroke: '#B5821F', text: 'text-warn', bar: 'bg-warn' },
  LOW: { label: 'Low', stroke: '#C14B33', text: 'text-bad', bar: 'bg-bad' },
};

/** Owner-level Business Health Score: a gauge for the overall number + sub-score bars. */
function BusinessHealthCard() {
  const { data, loading } = useAsync(getBusinessHealth);
  if (loading) return <Skeleton className="mb-3 h-48" />;
  if (!data) return null;
  const band = BAND[data.band];
  const r = 52;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(100, data.score)) / 100);
  return (
    <Card className="mb-3 p-5">
      <h2 className="font-display text-lg font-semibold text-ink">Business health</h2>
      <p className="text-sm text-muted">A rollup of retention, revenue, engagement and growth.</p>
      <div className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-center">
        <div className="relative mx-auto size-32 shrink-0 sm:mx-0">
          <svg viewBox="0 0 120 120" className="size-32 -rotate-90">
            <circle cx="60" cy="60" r={r} fill="none" stroke="#DCE0D2" strokeWidth="10" />
            <circle
              cx="60"
              cy="60"
              r={r}
              fill="none"
              stroke={band.stroke}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-3xl font-bold tabular text-ink">{data.score}</span>
            <span className={cn('text-xs font-semibold', band.text)}>{band.label}</span>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3">
          {data.subScores.map((s) => {
            const sb = BAND[s.band];
            return (
              <div key={s.key}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-soft">{s.label}</span>
                  <span className="font-medium tabular text-ink">{s.score}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface">
                  <div className={cn('h-full rounded-full', sb.bar)} style={{ width: `${s.score}%` }} />
                </div>
                <p className="mt-0.5 text-xs text-muted">{s.detail}</p>
              </div>
            );
          })}
        </div>
      </div>

      <details className="mt-4 border-t border-line pt-3 text-sm">
        <summary className="cursor-pointer font-medium text-ink">How this is calculated &amp; why it matters</summary>
        <div className="mt-3 flex flex-col gap-3 text-ink-soft">
          <p>
            One 0 to 100 number that answers "how is the business doing right now." It blends four things,
            weighted by how much each drives a gym's success:
          </p>
          <ul className="flex flex-col gap-2">
            <li>
              <span className="font-medium text-ink">Retention (35%).</span> The share of members who are healthy
              versus at risk of leaving. Keeping members costs far less than finding new ones, so it counts most.
            </li>
            <li>
              <span className="font-medium text-ink">Revenue (30%).</span> Income so far this month versus the same
              point last month.
            </li>
            <li>
              <span className="font-medium text-ink">Engagement (20%).</span> How many members actually show up.
              People who visit stay and renew.
            </li>
            <li>
              <span className="font-medium text-ink">New members (15%).</span> Sign-ups this month versus last.
            </li>
          </ul>
          <p>
            Each part, and the overall score, is banded <span className="font-medium text-ok">Good</span> (70+),{' '}
            <span className="font-medium text-warn">Watch</span> (40 to 69), or{' '}
            <span className="font-medium text-bad">Low</span> (under 40).
          </p>
          <p className="text-muted">
            Use it as a weekly gut-check: a falling score, or a red sub-score, tells you where to act before it
            reaches the bank. Tap a low area and work the member list behind it (Member health, Payments).
          </p>
        </div>
      </details>
    </Card>
  );
}
